import React, { useState } from 'react';
import {
  useNavigate,
} from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';

import NewStudentDialog from "./NewStudentDialog";
import LinkTableCell from "./LinkTableCell";
import AssignmentPill from "./AssignmentPill";
import { formatDate, formatDayRange, formatHumanTime, dummyData } from "./helpers";
import useSortHead from './useSortHead';

const ClassStudentsTable = ({ fetchClassData, className, classData }) => {

    const navigate = useNavigate();

    const { TableCellSortHead, sortFunction } = useSortHead('name');

    const [newStudentDialogOpen, setNewStudentDialogOpen] = useState(false);

    const handleClose = (modified) => {
        setNewStudentDialogOpen(false);
        if (modified) {
            fetchClassData();
        }
    };

    const openStudent = (studentEmail) => {
        navigate(`/classes/${classData.uuid}/students/${encodeURIComponent(studentEmail)}`, { state: { className } });
    };

    // The server's pick, judged on the school's calendar day — the same one
    // the per-student stats in this response are keyed on. Deriving it here
    // from the browser's local day can disagree with those stats.
    const currentAssignment = classData?.assignments?.find(
        (assignment) => assignment.uuid === classData.current_assignment_uuid
    ) ?? null;

    let dummyIndex = 0;
    const sortedStudents = (classData?.students ? classData.students : []).map((student, index) => {
        if (student.email.endsWith('@dummyschool.edu')) {
            const dummy = dummyData[dummyIndex % 32];
            dummyIndex++;
            const assignments = currentAssignment ? { [currentAssignment.uuid]: dummy.assignment } : [];
            // The demo rows carry only all-time figures; alias them so a
            // date-ranged class shows them too.
            student = {...student, ...dummy, total_time_in_range: dummy.total_time, assignments};
        }

        const last_active_sortkey = student.last_active_at === null ? '0000-00-00' : student.last_active_at;
        const assignment = currentAssignment ? { ...currentAssignment, ...student.assignments?.[currentAssignment.uuid] } : null;
        const assignment_sortkey = assignment ? assignment.progress : -1;
        return {...student, last_active_sortkey, assignment, assignment_sortkey};
    }).sort(sortFunction);

    const dateRange = classData ? formatDayRange(classData.start_date, classData.end_date) : null;
    const totalInputField = dateRange ? 'total_time_in_range' : 'total_time';
    const activeStudyTimeField = dateRange ? 'active_study_time_in_range' : 'active_study_time';
    const rangeText = dateRange ? ` ${dateRange}` : '';

    return (
        <>
            <div className="add-row-button">
                <Button
                    variant="text"
                    color="primary"
                    onClick={() => setNewStudentDialogOpen(true)}
                >
                    + Add Student
                </Button>
            </div>

            <Table>
                <TableHead>
                    <TableRow>
                        <TableCellSortHead field='name'>Student</TableCellSortHead>
                        <TableCellSortHead field='last_active_sortkey'>Last active</TableCellSortHead>
                        <TableCellSortHead field={totalInputField}>
                            <span title={'Total time spent studying in the app (including listening in the background)' + rangeText}>Total input</span>
                        </TableCellSortHead>
                        <TableCellSortHead field={activeStudyTimeField}>
                            <span title={'Total time spent actively studying in the app (with the app visible)' + rangeText}>Active study time</span>
                        </TableCellSortHead>
                        <TableCellSortHead field='assignment_sortkey'>Current assignment</TableCellSortHead>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedStudents.map(student => (
                        <TableRow
                            key={student.email}
                        >
                            <LinkTableCell onClick={() => openStudent(student.email)}>{student.name}</LinkTableCell>
                            <TableCell>{formatDate(student.last_active_at)}</TableCell>
                            <TableCell>{formatHumanTime(student[totalInputField])}</TableCell>
                            <TableCell>{formatHumanTime(student[activeStudyTimeField])}</TableCell>
                            <TableCell><AssignmentPill assignment={student.assignment} /></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <NewStudentDialog open={newStudentDialogOpen} onClose={handleClose} classId={classData.uuid} classStudents={classData.students} />
        </>
    );
};

export default ClassStudentsTable;
